import React, { useState, useMemo, useEffect } from 'react';
import AritoDataTables from '@/components/custom/arito/data-tables';
import { useSearchResults, useSelection } from './hooks';
import { getFormattedColumns } from './columns';
import { SearchTableFilterInput } from '../search-field-input';

interface DataTableProps<T = any> {
  searchEndpoint?: string;
  searchColumns?: any[];
  columnDisplay?: keyof T;
  otherSearchEndpoint?: string;
  rows?: T[];
  onRowSelection?: (row: T) => void;
  onSelectedObjChange?: (obj: T | null) => void;
}

export const DataTable = <T = any,>({
  searchEndpoint,
  otherSearchEndpoint,
  searchColumns = [],
  columnDisplay = '' as keyof T,
  rows,
  onRowSelection,
  onSelectedObjChange
}: DataTableProps<T>) => {
  const { searchResults, isLoading, totalItems, currentPage, handlePageChange, fetchSearchResults } =
    useSearchResults<T>(searchEndpoint, otherSearchEndpoint, { pageSize: 15 });
  const { selectedObj, selectedRowIndex, handleRowClick } = useSelection<T>();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Debounce search input to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchValue]);

  React.useEffect(() => {
    onSelectedObjChange?.(selectedObj);
  }, [selectedObj, onSelectedObjChange]);

  // Get the source data (either provided rows or search results)
  const sourceData = rows || searchResults;

  // Filter data based on debounced search value (only for local data, not for API data)
  const filteredData = useMemo(() => {
    // If we have searchEndpoint, use server-side search, so no client-side filtering needed
    if (searchEndpoint || otherSearchEndpoint) {
      return sourceData;
    }

    // For local data (rows prop), do client-side filtering
    if (!debouncedSearchValue.trim()) {
      return sourceData;
    }

    const searchTerm = debouncedSearchValue.toLowerCase().trim();

    const filtered = sourceData.filter((row: any) => {
      // Search through all fields in the row
      const matches = Object.values(row).some((value: any) => {
        if (value === null || value === undefined) return false;

        // Convert value to string and search
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(searchTerm);
      });
      return matches;
    });

    return filtered;
  }, [sourceData, debouncedSearchValue, searchEndpoint, otherSearchEndpoint]);

  // Handle search when debouncedSearchValue changes
  useEffect(() => {
    if (searchEndpoint || otherSearchEndpoint) {
      // Call API with search parameter
      fetchSearchResults(searchEndpoint, otherSearchEndpoint, debouncedSearchValue, { page: 0, pageSize: 15 });
    }
  }, [debouncedSearchValue, searchEndpoint, otherSearchEndpoint, fetchSearchResults]);

  // Use server-side pagination
  const pageSize = 15;

  const tableData = [
    {
      name: '',
      rows: filteredData,
      columns: getFormattedColumns(searchColumns)
    }
  ];

  return (
    <>
      <div className='flex items-center border-b border-gray-200 px-4 py-2'>
        <SearchTableFilterInput value={searchValue} onChange={e => setSearchValue(e.target.value)} />
      </div>

      <div className='flex size-full min-h-[600px] flex-col border border-[#eaeaea] bg-white'>
        {isLoading && (
          <div className='flex h-64 items-center justify-center'>
            <div className='h-8 w-8 animate-spin rounded-full border-2 border-[rgba(15,118,110,0.9)] border-t-transparent'></div>
          </div>
        )}

        {!isLoading && (
          <AritoDataTables
            tables={tableData}
            onRowClick={(params: any) => {
              handleRowClick(params);
              onRowSelection?.(params.row);
            }}
            selectedRowId={selectedRowIndex || undefined}
            getRowId={row => row.uuid || String(row[columnDisplay])}
            pageSize={pageSize}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={page => handlePageChange(page, debouncedSearchValue)}
            serverSidePagination={true}
          />
        )}
      </div>
    </>
  );
};
